/**
 * StaffMessagesInbox — M2.3.2 · Selezione multipla + bulk mark-read.
 *
 * UX:
 *  - Toggle "seleziona" attiva checkbox per riga.
 *  - Barra azioni bulk: "Segna letta", "Nascondi", "Deseleziona".
 *  - "Segna tutte come lette" con conferma esplicita.
 *  - Wording: "Non letti in Sarabi Staff" (non "unread WhatsApp").
 */
import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, ArrowLeft, ArrowUpRight, ArrowDownLeft,
         RefreshCw, UserPlus, EyeOff, CheckCheck, X, Trash2, AlertTriangle, Mail } from "lucide-react";
import { useMessagingPoll } from "@/lib/useMessagingPoll";
import { AudioStatusBar } from "@/components/AudioStatusBar";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const POLL_MS = 8000;
const PAGE_SIZE = 50;

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "adesso";
  if (diff < 3600) return `${Math.floor(diff / 60)}m fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h fa`;
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
}

function formatWaId(waId) {
  if (!waId) return "";
  const s = String(waId).replace(/^\+/, "");
  if (s.length < 7) return "+" + s;
  const cc = s.slice(0, 2);
  const rest = s.slice(cc.length);
  const groups = [];
  let i = 0;
  while (i < rest.length) {
    const take = (rest.length - i <= 4) ? rest.length - i : 3;
    groups.push(rest.slice(i, i + take));
    i += take;
  }
  return `+${cc} ${groups.join(" ")}`;
}

function displayName(row) {
  if (row.customer_name) return row.customer_name;
  if (row.display_name) return row.display_name;
  return formatWaId(row.wa_id_e164 || ("+" + (row.wa_id || "")));
}

export default function StaffMessagesInbox() {
  const nav = useNavigate();
  const [hidingId, setHidingId] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // {id, name}
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Filtro "Da leggere" (server-side only_unread) + paginazione cursor-based.
  // La prima pagina è gestita dal poller; le pagine successive ("Carica
  // altre") sono accumulate in `extraPages` e deduplicate per id.
  const [onlyUnread, setOnlyUnread] = useState(
    () => sessionStorage.getItem("inbox.onlyUnread") === "1");
  const [extraPages, setExtraPages] = useState([]);
  const [moreCursor, setMoreCursor] = useState(undefined); // undefined = usa data.next_cursor
  const [loadingMore, setLoadingMore] = useState(false);
  const firstRender = useRef(true);

  const { data, error, loading, refresh } = useMessagingPoll(
    "/staff/messaging/whatsapp/conversations",
    { intervalMs: POLL_MS,
      params: onlyUnread ? { limit: PAGE_SIZE, only_unread: true } : { limit: PAGE_SIZE } });

  useEffect(() => {
    sessionStorage.setItem("inbox.onlyUnread", onlyUnread ? "1" : "0");
    if (firstRender.current) { firstRender.current = false; return; }
    setExtraPages([]); setMoreCursor(undefined);
    refresh();
  }, [onlyUnread]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetPages = () => { setExtraPages([]); setMoreCursor(undefined); };

  const items = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const c of [...(data?.items || []), ...extraPages.flat()]) {
      if (seen.has(c.id)) continue;
      if (onlyUnread && !(c.unread_count > 0)) continue;
      seen.add(c.id); out.push(c);
    }
    return out;
  }, [data, extraPages, onlyUnread]);

  const nextCursor = moreCursor === undefined ? data?.next_cursor : moreCursor;

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const { data: r } = await api.get("/staff/messaging/whatsapp/conversations", {
        params: { limit: PAGE_SIZE, cursor: nextCursor,
                  ...(onlyUnread ? { only_unread: true } : {}), _t: Date.now() } });
      setExtraPages((p) => [...p, r.items || []]);
      setMoreCursor(r.next_cursor || null);
    } catch (err) {
      toast.error(formatApiError(err) || "Impossibile caricare altre conversazioni");
    } finally { setLoadingMore(false); }
  };
  const totalUnread = useMemo(
    () => items.reduce((s, c) => s + (c.unread_count || 0), 0),
    [items]);
  const unreadRowsCount = useMemo(
    () => items.filter((c) => (c.unread_count || 0) > 0).length,
    [items]);

  const toggleSel = (convId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(convId)) next.delete(convId); else next.add(convId);
      return next;
    });
  };
  const clearSel = () => { setSelected(new Set()); setSelectMode(false); };
  const selectAll = () => { setSelected(new Set(items.map((c) => c.id))); };

  const doHide = async (convId, e) => {
    if (e) e.stopPropagation();
    if (hidingId) return;
    setHidingId(convId);
    try {
      await api.post(`/staff/messaging/whatsapp/conversations/${convId}/hide`);
      toast.success("Conversazione nascosta");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err) || "Impossibile nascondere");
    } finally { setHidingId(null); }
  };

  const doDelete = async () => {
    if (!deleteTarget || deleteBusy) return;
    setDeleteBusy(true);
    try {
      const { data: r } = await api.delete(
        `/staff/messaging/whatsapp/conversations/${deleteTarget.id}`);
      if (r?.deleted) {
        toast.success("Chat cancellata definitivamente");
      } else {
        toast.info("La chat non esisteva più");
      }
      setDeleteTarget(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err) || "Impossibile cancellare la chat");
    } finally { setDeleteBusy(false); }
  };

  const doBulkMarkRead = async () => {
    if (bulkBusy || selected.size === 0) return;
    setBulkBusy(true);
    try {
      const { data: r } = await api.post(
        "/staff/messaging/whatsapp/conversations/bulk-mark-read",
        { conversation_ids: [...selected] });
      toast.success(`Segnate come lette: ${r.updated}`);
      clearSel(); refresh();
    } catch (err) {
      toast.error(formatApiError(err) || "Errore bulk mark-read");
    } finally { setBulkBusy(false); }
  };

  const doBulkHide = async () => {
    if (bulkBusy || selected.size === 0) return;
    setBulkBusy(true);
    try {
      // No dedicated bulk endpoint yet; loop over selection.
      // Idempotente lato server, sicuro.
      const ids = [...selected];
      await Promise.allSettled(ids.map((id) =>
        api.post(`/staff/messaging/whatsapp/conversations/${id}/hide`)));
      toast.success(`Nascoste: ${ids.length}`);
      clearSel(); refresh();
    } catch (err) {
      toast.error(formatApiError(err) || "Errore nascondi bulk");
    } finally { setBulkBusy(false); }
  };

  const doMarkAllRead = async () => {
    if (bulkBusy) return;
    const c = window.confirm(
      `Segnare tutte le conversazioni con non letti (${unreadRowsCount}) come lette in Sarabi Staff?\n\n`
      + `Questo è utile come baseline iniziale dopo l'aggiornamento. `
      + `I nuovi messaggi che arriveranno saranno contati regolarmente.\n\n`
      + `Non cancella nessun messaggio.`);
    if (!c) return;
    setBulkBusy(true);
    try {
      const { data: r } = await api.post(
        "/staff/messaging/whatsapp/conversations/mark-all-read");
      toast.success(`Baseline reset: ${r.updated} conversazioni segnate come lette`);
      clearSel(); resetPages(); refresh();
    } catch (err) {
      toast.error(formatApiError(err) || "Errore mark-all-read");
    } finally { setBulkBusy(false); }
  };

  return (
    <div data-testid="staff-messages-inbox"
         className="max-w-4xl mx-auto font-sans">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => nav("/staff")}
                data-testid="messages-back-btn"
                className="p-2 -ml-2 rounded-full hover:bg-sand-100">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="eyebrow">Servizio</div>
          <h1 className="font-serif text-3xl mt-1">Messaggi</h1>
          {totalUnread > 0 && (
            <div className="text-xs text-ink-500 mt-0.5">
              {totalUnread} non letti in Sarabi Staff · {unreadRowsCount} conversazioni
            </div>
          )}
        </div>
        <button onClick={refresh} title="Aggiorna"
                data-testid="messages-refresh-btn"
                className="p-2 rounded-full hover:bg-sand-100">
          <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <AudioStatusBar />
        <button
          data-testid="inbox-only-unread-toggle"
          aria-pressed={onlyUnread}
          onClick={() => setOnlyUnread((v) => !v)}
          className={`text-xs px-3 py-1.5 rounded-full border-2 inline-flex items-center gap-1
                      ${onlyUnread ? "border-bronze-600 bg-bronze-600 text-white"
                                   : "border-sand-300 hover:bg-sand-100 text-ink-700"}`}>
          <Mail className="w-3.5 h-3.5" /> Da leggere
        </button>
        <button
          data-testid="inbox-select-toggle"
          onClick={() => selectMode ? clearSel() : setSelectMode(true)}
          className="ml-auto text-xs px-3 py-1.5 rounded-full border-2 border-sand-300
                      hover:bg-sand-100 text-ink-700">
          {selectMode ? "Annulla selezione" : "Seleziona"}
        </button>
        <button
          data-testid="inbox-mark-all-read"
          onClick={doMarkAllRead}
          disabled={bulkBusy || unreadRowsCount === 0}
          className="text-xs px-3 py-1.5 rounded-full border-2 border-bronze-300
                      bg-bronze-50 hover:bg-bronze-100 text-bronze-800
                      disabled:opacity-40 disabled:cursor-not-allowed">
          Segna tutte come lette
        </button>
      </div>

      {selectMode && (
        <div data-testid="bulk-actions-bar"
             className="mb-3 p-3 rounded-2xl bg-bronze-50 border-2 border-bronze-200
                        flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-bronze-900">
            {selected.size} selezionate
          </span>
          <button
            data-testid="bulk-select-all"
            onClick={selectAll}
            className="text-xs px-3 py-1.5 rounded-full bg-white border border-bronze-300 hover:bg-bronze-50">
            Seleziona tutte
          </button>
          <div className="flex-1" />
          <button
            data-testid="bulk-mark-read"
            onClick={doBulkMarkRead}
            disabled={bulkBusy || selected.size === 0}
            className="text-xs px-3 py-1.5 rounded-full bg-bronze-600 text-white
                        hover:bg-bronze-700 inline-flex items-center gap-1
                        disabled:opacity-40 disabled:cursor-not-allowed">
            <CheckCheck className="w-3.5 h-3.5" /> Segna lette
          </button>
          <button
            data-testid="bulk-hide"
            onClick={doBulkHide}
            disabled={bulkBusy || selected.size === 0}
            className="text-xs px-3 py-1.5 rounded-full bg-white border border-sand-300 hover:bg-sand-100
                        inline-flex items-center gap-1
                        disabled:opacity-40 disabled:cursor-not-allowed">
            <EyeOff className="w-3.5 h-3.5" /> Nascondi
          </button>
          <button
            data-testid="bulk-clear"
            onClick={clearSel}
            className="p-1.5 rounded-full hover:bg-white text-bronze-900">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {loading && items.length === 0 && (
        <div data-testid="messages-loading" className="text-ink-500 text-sm">Caricamento…</div>
      )}
      {error && (
        <div data-testid="messages-error"
             className="rounded-2xl border-2 border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      )}
      {!loading && !error && items.length === 0 && (
        <div data-testid="messages-empty" className="rounded-3xl border-2 border-sand-300 bg-white p-10 text-center">
          <MessageSquare className="w-10 h-10 mx-auto text-sand-400" strokeWidth={1.5} />
          <div className="mt-3 text-ink-700">
            {onlyUnread ? "Nessuna conversazione da leggere" : "Nessuna conversazione WhatsApp"}
          </div>
        </div>
      )}

      {items.length > 0 && (
        <ul className="divide-y divide-sand-200 rounded-3xl border-2 border-sand-300 bg-white overflow-hidden">
          {items.map((c) => {
            const unread = (c.unread_count || 0) > 0;
            const dir = c.last_direction;
            const DirIcon = dir === "out" ? ArrowUpRight : ArrowDownLeft;
            const isUnmatched = !c.customer_id;
            const isAmbiguous = isUnmatched && c.customer_match_status === "ambiguous";
            const isSel = selected.has(c.id);
            return (
              <li key={c.id}
                  data-testid={`conversation-row-${c.id}`}
                  data-unread={unread ? "true" : "false"}
                  data-matched={c.customer_id ? "true" : "false"}
                  onClick={() => selectMode ? toggleSel(c.id) : nav(`/staff/messages/${c.id}`)}
                  className={`flex items-center gap-3 p-4 cursor-pointer active:bg-sand-50
                              ${isSel ? "bg-bronze-100" :
                                unread ? "bg-bronze-50/40" : "bg-white"}`}>
                {selectMode && (
                  <input
                    type="checkbox"
                    data-testid={`conv-checkbox-${c.id}`}
                    checked={isSel}
                    readOnly
                    className="w-4 h-4 accent-bronze-600 flex-shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0
                                  ${unread ? "bg-bronze-100 border-2 border-bronze-300"
                                            : "bg-sand-100 border border-sand-300"}`}>
                  <MessageSquare className={`w-5 h-5 ${unread ? "text-bronze-700" : "text-ink-500"}`}
                                    strokeWidth={1.8} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span data-testid={`conv-name-${c.id}`}
                          className={`truncate text-[15px] ${unread ? "font-semibold text-ink-900" : "font-medium text-ink-800"}`}>
                      {displayName(c)}
                    </span>
                    {isUnmatched && !selectMode && (
                      <span
                        role="button"
                        data-testid={isAmbiguous ? `verify-crm-inbox-${c.id}` : `add-crm-inbox-${c.id}`}
                        title={isAmbiguous ? "Più clienti CRM con questo numero: verifica manuale" : "Aggiungi al CRM"}
                        onClick={(e) => {
                          e.stopPropagation();
                          nav(`/staff/messages/${c.id}?add=1`);
                        }}
                        className="inline-flex items-center gap-1 text-xs text-bronze-700
                                    px-2 py-0.5 rounded-full bg-bronze-50 border border-bronze-200
                                    hover:bg-bronze-100">
                        <UserPlus className="w-3 h-3" strokeWidth={2.2} />
                        <span>{isAmbiguous ? "Verifica CRM" : "CRM"}</span>
                      </span>
                    )}
                    <span className="text-xs text-ink-400 flex-shrink-0 ml-auto">
                      {timeAgo(c.last_message_at)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {dir && <DirIcon className="w-3.5 h-3.5 text-ink-400 flex-shrink-0" />}
                    <span className={`text-sm truncate ${unread ? "text-ink-800" : "text-ink-500"}`}>
                      {c.last_message_preview || "—"}
                    </span>
                  </div>
                </div>
                {unread && (
                  <span data-testid={`unread-badge-${c.id}`}
                        title="Non letti in Sarabi Staff"
                        className="ml-2 min-w-[22px] h-[22px] px-2 rounded-full bg-bronze-600
                                    text-white text-xs font-semibold flex items-center justify-center">
                    {c.unread_count}
                  </span>
                )}
                {!selectMode && (
                  <>
                    <button
                      type="button"
                      title="Nascondi conversazione (torna con un nuovo messaggio)"
                      data-testid={`conv-hide-${c.id}`}
                      disabled={hidingId === c.id}
                      onClick={(e) => doHide(c.id, e)}
                      className="ml-1 p-1.5 rounded-full text-ink-400 hover:bg-sand-100
                                  hover:text-ink-700 disabled:opacity-40 disabled:cursor-not-allowed">
                      <EyeOff className="w-4 h-4" strokeWidth={1.8} />
                    </button>
                    <button
                      type="button"
                      title="Cancella chat da Sarabi Staff (definitivo)"
                      data-testid={`conv-delete-${c.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget({ id: c.id, name: displayName(c) });
                      }}
                      className="ml-0.5 p-1.5 rounded-full text-ink-400 hover:bg-red-50
                                  hover:text-red-600">
                      <Trash2 className="w-4 h-4" strokeWidth={1.8} />
                    </button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {items.length > 0 && nextCursor && (
        <div className="mt-3 flex justify-center">
          <button
            data-testid="inbox-load-more"
            onClick={loadMore}
            disabled={loadingMore}
            className="text-sm px-5 py-2 rounded-full border-2 border-sand-300 bg-white
                        hover:bg-sand-100 text-ink-700 disabled:opacity-40">
            {loadingMore ? "Caricamento…" : "Carica altre conversazioni"}
          </button>
        </div>
      )}

      {deleteTarget && (
        <div
          data-testid="delete-conv-modal-overlay"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center
                     bg-black/40 backdrop-blur-sm p-4"
          onClick={() => !deleteBusy && setDeleteTarget(null)}>
          <div
            data-testid="delete-conv-modal"
            className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="mt-0.5 w-10 h-10 rounded-full bg-red-50
                              flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h2 className="font-serif text-xl text-ink-900">
                  Cancella definitivamente questa chat?
                </h2>
                <p className="text-sm text-ink-600 mt-2 leading-relaxed">
                  Verranno eliminati da Sarabi Staff la conversazione e tutti
                  i messaggi associati con <span className="font-medium text-ink-800">
                    {deleteTarget.name}</span>. Questa operazione non può essere
                  annullata.
                </p>
                <p className="text-xs text-ink-500 mt-2"
                    data-testid="delete-conv-scope-note">
                  Questa operazione elimina la conversazione solo da Sarabi OS
                  e non da WhatsApp Business.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                data-testid="delete-conv-cancel"
                onClick={() => setDeleteTarget(null)}
                disabled={deleteBusy}
                className="text-sm px-4 py-2 rounded-full border-2 border-sand-300
                            hover:bg-sand-50 text-ink-700
                            disabled:opacity-40 disabled:cursor-not-allowed">
                Annulla
              </button>
              <button
                data-testid="delete-conv-confirm"
                onClick={doDelete}
                disabled={deleteBusy}
                className="text-sm px-4 py-2 rounded-full bg-red-600 hover:bg-red-700
                            text-white font-medium inline-flex items-center gap-1.5
                            disabled:opacity-40 disabled:cursor-not-allowed">
                <Trash2 className="w-4 h-4" />
                {deleteBusy ? "Cancellazione…" : "Cancella definitivamente"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
